import { NextPage } from "next";
import { useIntl } from "react-intl";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useRouter } from "next/router";
import { routes } from "@/routes/routes";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { CtaButtonWithLoader } from "@/components/common/ctaButtonWithLoader/CtaButtonWithLoader";
import { TextInput } from "@/components/common/textInput/TextInput";
import { Modal } from "@/components/common/modal/Modal";
import { sendFetchRequest } from "@/utils/sendFetchRequest";
import { Tooltip } from "@/components/common/Tooltip/Tooltip";
import { NetworkError } from "@/interfaces/errors/networkError";
import styles from "./Rental.module.scss";

dayjs.extend(customParseFormat);

type ConnectError = {
  errorCode: number;
  message: string;
  requestId: string;
  errorDetailsObject: null;
};

type RentalProps = PageProps & {
  defaultCanonicalRoute: string;
};

export const Rental: NextPage<RentalProps> = ({
  layout,
  defaultCanonicalRoute,
}: RentalProps) => {
  const intl = useIntl();
  const router = useRouter();
  const emailV2 = true;

  const [rentalID, setRentalID] = useState<string>("");
  const [identification, setIdentification] = useState<string>("");
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<number>();
  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [plateNumberFromUrl, setPlateNumberFromUrl] = useState<string>("");

  const [showIdentificationInfo, setShowIdentificationInfo] =
    useState<boolean>(false);

  const getQueryParams = (): Partial<Record<string, string>> => {
    const queryString = router.asPath.split("?")[1];
    if (!queryString) {
      return {};
    }

    const cleanedQueryString = queryString.split("#")[0];
    const queryParams: Record<string, string> = {};
    const decodeParamValue = (value: string) => {
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    };

    cleanedQueryString.split("&").forEach((param) => {
      if (!param) {
        return;
      }

      const [key, ...valueParts] = param.split("=");
      if (!key) {
        return;
      }

      const rawValue = valueParts.join("=");
      queryParams[key] = rawValue ? decodeParamValue(rawValue) : "";
    });

    return queryParams;
  };

  const next = () => {
    if (isValid) {
      setIsLoading(true);

      const fieldType = identification.includes("@") ? "Email" : "Name";
      const bookingReferenceTypeParam = plateNumberFromUrl
        ? "&BookingReferenceType=PlateNumber"
        : "";

      if (fieldType === "Email") {
        sessionStorage.setItem("email", identification);
      }

      const isLink = !!rentalID.match(/^[A-Za-z]{5}[0-9]{3}$/);

      sendFetchRequest(
        `/customers/v1/checkin/checkCustomerEmailAndBooking?documentId=${rentalID}&SearchValue=${encodeURIComponent(
          identification
        )}&FieldType=${fieldType}${bookingReferenceTypeParam}&link=${
          isLink ? "true" : "false"
        }`
      )
        .then((response: string) => {
          void router.push({
            pathname: routes.myLocation(response),
          });
        })
        .catch((e: NetworkError<ConnectError>) => {
          setIsLoading(false);
          setIsError(e.body?.errorCode);
        });
    }
  };

  useEffect(() => {
    setIsValid(rentalID.length > 0 && identification.length > 0);
  }, [rentalID, identification]);

  useEffect(() => {
    const queryParams = getQueryParams();
    const plateNumber =
      queryParams["PlateNumber"] ??
      queryParams["plateNumber"] ??
      queryParams["platenumber"] ??
      "";
    const reservationId = queryParams["reservationId"] ?? "";
    const email = queryParams["email"] ?? "";
    const hasPlateNumber = plateNumber.length > 0;
    const hasReservationId = reservationId.length > 0;
    const hasEmail = email.length > 0;

    setPlateNumberFromUrl(plateNumber);

    if (hasPlateNumber && rentalID === "") {
      setRentalID(plateNumber);
    }

    if (hasReservationId && hasEmail) {
      setRentalID(reservationId);
      setIdentification(email);

      next();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailV2, router.asPath]);

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        title={intl.formatMessage({ id: "manageRental" })}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: `manageRental` }),
            url: defaultCanonicalRoute,
          },
        ]}
      >
        <div className={styles.section}>
          <div className={styles.title}>
            {intl.formatMessage({ id: "manageRental" })}
          </div>
          <div className={styles.subtitle}>
            {intl.formatMessage({ id: "manageRentalSubtitle" })}
          </div>
          <div className={styles.form}>
            <div className={styles.inputGroup}>
              <div className={styles.label}>
                {intl.formatMessage({
                  id: plateNumberFromUrl ? "rentalIDPlateNumber" : "rentalID",
                })}{" "}
                *
              </div>
              <div className={styles.input}>
                <TextInput
                  value={rentalID}
                  setValue={setRentalID}
                  isError={isError !== undefined}
                />
              </div>
            </div>
            <div className={styles.inputGroup}>
              <div className={styles.label}>
                {intl.formatMessage({ id: "identification" })} *
                <div
                  className={styles.infoIconContainer}
                  onMouseEnter={() => setIsHovering(true)}
                  onMouseLeave={() => setIsHovering(false)}
                >
                  <svg
                    width="16"
                    height="16"
                    className={`${styles.icon} ${
                      isHovering ? styles.iconHover : ""
                    }`}
                    onClick={() =>
                      setShowIdentificationInfo(!showIdentificationInfo)
                    }
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="7.5"
                      fill={isHovering ? "#062E52" : ""}
                      stroke="#062E52"
                    />
                    <path
                      d="M6 6.387c.165-.464.484-.854.902-1.102a2.026 2.026 0 0 1 1.385-.255c.477.084.91.336 1.223.712.313.376.486.853.49 1.347 0 1.403-2.062 2.104-2.062 2.104M7.993 12H8z"
                      fill={isHovering ? "#062E52" : "#F8F8FE"}
                    />
                    <path
                      d="M6 6.387c.165-.464.484-.854.902-1.102a2.026 2.026 0 0 1 1.385-.255c.477.084.91.336 1.223.712.313.376.486.853.49 1.347 0 1.403-2.062 2.104-2.062 2.104v1.404M7.993 12H8"
                      stroke={isHovering ? "#F8F8FE" : "#062E52"}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <Tooltip
                    content={intl.formatMessage({
                      id: "identificationInfoDescription",
                    })}
                    isVisible={showIdentificationInfo}
                  />
                </div>
              </div>
              <div className={styles.input}>
                <TextInput
                  value={identification}
                  setValue={setIdentification}
                  isError={isError !== undefined}
                />
              </div>
            </div>
            {(isError === 15100 || isError === 15101) && (
              <div className={styles.error}>
                {intl.formatMessage({ id: "checkInError" })}
              </div>
            )}
            {isError === 15103 && (
              <div className={styles.error}>
                {intl.formatMessage(
                  { id: "checkInB2BError" },
                  {
                    br: () => <br />,
                  }
                )}
              </div>
            )}
            <a className={styles.forgotID} onClick={() => setModalOpened(true)}>
              {intl.formatMessage({ id: "forgotID" })}
            </a>
            <Modal
              isOpen={modalOpened}
              setIsOpen={setModalOpened}
              title={intl.formatMessage({ id: "forgotID" })}
              overrideStyles={{
                title: styles.modalTitle,
                close: styles.modalClose,
              }}
            >
              <div className={styles.modalDescription}>
                {intl.formatMessage(
                  { id: "forgotIDDescription" },
                  {
                    br: () => <br />,
                  }
                )}
              </div>
            </Modal>
          </div>
          <div className={styles.confirm}>
            <CtaButtonWithLoader
              label={intl.formatMessage({ id: "next" })}
              onClick={next}
              isDisabled={!isValid}
              isLoading={isLoading}
            />
          </div>
        </div>
      </Layout>
    </>
  );
};
